/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import { TextareaInput, TextInput } from 'components/FormControl';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addHardProductsSchema } from 'features/Project/validation';
import CustomFileInput from 'components/FormControl/CustomFileInput';
import { formatFileNameProduct } from 'utils/func';
import { PRODUCT_TYPE } from 'constant/summary-report.constant';
import PropTypes from 'prop-types';

const formOptions = { resolver: yupResolver(addHardProductsSchema) };

const HardProductDialog = forwardRef(({ handleDownloadFile, productTypeShort }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const toast = useRef(null);

  const [hardProductControl, setHardProductControl] = useState();
  const [visible, setVisible] = useState(false);

  const {
    control,
    getValues,
    reset,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });
  // #endregion Data

  // #region Event
  const showError = (message) => {
    toast.current.show({
      severity: 'error',
      summary: t('toast.error'),
      detail: message,
      life: 4000,
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (_hardProductControl) => {
        setHardProductControl(_hardProductControl);
        setVisible(true);
        if (_hardProductControl.type === 'INSERT') {
          reset({});
        } else if (_hardProductControl.type === 'UPDATE') {
          reset(_hardProductControl.value);
        }
      },
    }),
    []
  );

  const handleAddHardProduct = async () => {
    const isValidTrigger = await trigger();
    if (!isValidTrigger) {
      showError(t('errorMessage.validationErrorMessage'));
      return;
    }

    const data = getValues();

    const { insert, update, index, type } = hardProductControl;

    if (type === 'INSERT' && insert) {
      insert(index, data);
    } else if (type === 'UPDATE' && update) {
      update(index, { ...data, isUpdateNewValue: true });
    }
    reset({});
    setVisible(false);
  };

  // #endregion Event

  return (
    <>
      <Dialog
        header={
          hardProductControl?.type === 'INSERT'
            ? t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.addHardProduct'
              )
            : t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.updateHardProduct'
              )
        }
        visible={visible}
        onHide={() => {
          setVisible(false);
        }}
        draggable={false}
        className="w-full sm:w-7 md:w-6 lg:w-5"
      >
        <div className="grid p-fluid">
          <div className="col-12">
            <TextareaInput
              name="productDetail.name"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.productName'
              )}
              isRequired
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <TextareaInput
              name="productDetail.qualityCriteria"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.qualityCriteria'
              )}
              isRequired
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <TextInput
              name="productDetail.measure"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.measure'
              )}
              isRequired
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <TextareaInput
              name="productDetail.evaluativeCriteria"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.evaluativeCriteria'
              )}
              isRequired
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <TextareaInput
              name="productDetail.domesticModelQuality"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.similarDomesticModelQualityLevel'
              )}
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <TextareaInput
              name="productDetail.internationalModelQuality"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.similarInternationalModelQualityLevel'
              )}
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <TextareaInput
              name="productDetail.expectedQuantity"
              label={t(
                'projectProposal.researchResult.productAndResearchResultDes.hardProduct.fields.expectedQuantity'
              )}
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <CustomFileInput
              name="file"
              label={t('submitDocument.product.evidenceFile')}
              defaultNameFile={`${formatFileNameProduct(
                'a.pdf',
                hardProductControl?.projectCode,
                productTypeShort,
                !hardProductControl?.index ? 1 : parseInt(hardProductControl?.index, 10) + 1
              )}`}
              isRequired
              control={control}
              errors={errors}
              accept=".pdf"
              maxFileSize={30000000}
            />
            {hardProductControl?.value?.fileUrl ? (
              <span
                className="p-button p-button-text p-button-sm mt-1 p-0"
                onClick={
                  () =>
                    handleDownloadFile(
                      hardProductControl?.value?.fileUrl,
                      formatFileNameProduct(
                        hardProductControl?.value?.fileUrl || '',
                        hardProductControl?.projectCode,
                        productTypeShort,
                        !hardProductControl?.index ? 1 : parseInt(hardProductControl?.index, 10) + 1
                      )
                    )
                  // eslint-disable-next-line react/jsx-curly-newline
                }
              >
                <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                <span className="text-blue-500 underline font-italic ml-1">
                  {formatFileNameProduct(
                    hardProductControl?.value?.fileUrl || '',
                    hardProductControl?.projectCode,
                    productTypeShort,
                    !hardProductControl?.index ? 1 : parseInt(hardProductControl?.index, 10) + 1
                  )}
                </span>
              </span>
            ) : (
              <span>
                {hardProductControl?.value?.file?.name &&
                  formatFileNameProduct(
                    hardProductControl?.value?.file?.name || '',
                    hardProductControl?.projectCode,
                    productTypeShort,
                    !hardProductControl?.index ? 1 : parseInt(hardProductControl?.index, 10) + 1
                  )}
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label={t('formLayout.action.cancel')}
            severity="danger"
            onClick={() => {
              setVisible(false);
            }}
            className="mr-1 w-8rem"
          />
          <Button
            label={
              hardProductControl?.type === 'INSERT'
                ? t('formLayout.action.add')
                : t('formLayout.action.update')
            }
            type="submit"
            severity="info"
            onClick={handleAddHardProduct}
            className="w-8rem"
            disabled={!Object.keys(dirtyFields)?.length}
          />
        </div>
      </Dialog>
      <Toast ref={toast} />
    </>
  );
});

HardProductDialog.propTypes = {
  handleDownloadFile: PropTypes.func.isRequired,
  productTypeShort: PropTypes.string,
};

HardProductDialog.defaultProps = {
  productTypeShort: PRODUCT_TYPE.HARD_PRODUCT.short,
};

export default HardProductDialog;
